/**
 * @description 购物车套餐行（`comboVO.matchingProds` / `mainProd`）数量与删除约束
 */

import {
  isComboMatchingProdRequired,
  resolveComboProdLeastNum,
  resolveComboProdMaxNum,
} from '~/utils/product'
import { normalizeComboBundleItemQty, normalizeComboBundleVo } from '~/utils/store/promotionDetail'
import { isCartLinePromotionActivityInProgress } from '~/utils/store/promotionActivityInProgress'
import { isComboBundle, isComboPackage } from '~/utils/store/promotionTypes'
import { hasSiblingSkuForSameProduct } from '~/utils/store/orderConfirmBuyNowItems'
import { type3PackageGroupKeyFromRow } from '~/utils/store/type3PackageGroupKey'

/**
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
const sameId = (a, b) => a != null && b != null && String(a) === String(b)

/**
 * @description 套餐 VO 搭配品列表
 * @param {Object|null|undefined} comboVO
 * @returns {Array<Object>}
 */
const normalizeComboMatchingList = (comboVO) => {
  if (!comboVO || typeof comboVO !== 'object') return []
  const raw =
    comboVO.matchingProds ||
    comboVO.comboProducts ||
    comboVO.matchingProdList ||
    comboVO.comboMatchProds ||
    comboVO.comboMatchingProds ||
    comboVO.matchProds
  return Array.isArray(raw) ? raw : []
}

/**
 * @description 在 `matchingProds` 中按 prodId / skuId 匹配套餐商品配置
 * @param {Array<Object>} list
 * @param {*} prodId
 * @param {*} skuId
 * @returns {Object|null}
 */
const findComboProdInMatchingList = (list, prodId, skuId) => {
  if (!Array.isArray(list) || !list.length) return null
  return (
    list.find(
      (m) =>
        m &&
        typeof m === 'object' &&
        (sameId(m.prodId, prodId) || sameId(m.productId, prodId)) &&
        (m.skuId == null || m.skuId === '' || sameId(m.skuId, skuId))
    ) ||
    list.find((m) => m && typeof m === 'object' && sameId(m.prodId, prodId)) ||
    null
  )
}

/**
 * @description 解析套餐商品在 comboVO 中的活动库存（`matchingProds` / `mainProd` 的 `stocks` 等）
 * @param {Object|null|undefined} comboMeta
 * @returns {number|null}
 */
export const resolveComboProdStockCap = (comboMeta) => {
  if (!comboMeta || typeof comboMeta !== 'object') return null
  const raw =
    comboMeta.stocks ??
    comboMeta.stock ??
    comboMeta.skuStock ??
    comboMeta.activityStock ??
    comboMeta.comboStock
  if (raw === null || raw === undefined || raw === '') return null
  const n = Math.trunc(Number(raw))
  return Number.isFinite(n) && n >= 0 ? n : null
}

/**
 * @description 从套餐组 leader 的 `comboVO` 解析当前购物车行对应的套餐商品配置（主品 / 搭配品）
 * @reason 主商品除 `mainProd` 外，接口常把主品起购/限购/库存写在 `matchingProds` 对应项
 * @param {Object} line - V3 shopCartItem 或扁平行
 * @param {Object|null|undefined} comboVO - 主行 `comboVO`
 * @returns {Object|null}
 */
export const resolveCartComboProdMetaFromComboVO = (line, comboVO) => {
  if (!line || typeof line !== 'object' || !comboVO || typeof comboVO !== 'object') return null

  const normalizedComboVO = normalizeComboBundleVo(comboVO)
  const pv = line.productVO || {}
  const sv = line.skuVO || {}
  const prodId = line.prodId ?? pv.prodId
  const skuId = line.skuId ?? sv.skuId
  const list = normalizeComboMatchingList(normalizedComboVO)
  const fromMatching = findComboProdInMatchingList(list, prodId, skuId)

  const isMainLine =
    line.comboVO != null || line.isComboPackageMain === true || line.comboItemType === 1

  if (isMainLine) {
    if (fromMatching) return normalizeComboBundleItemQty(fromMatching)
    const main =
      normalizedComboVO.mainProd ??
      normalizedComboVO.mainProduct ??
      normalizedComboVO.main ??
      (normalizedComboVO.mainProdId != null && sameId(normalizedComboVO.mainProdId, prodId)
        ? normalizedComboVO
        : null)
    return main && typeof main === 'object' ? normalizeComboBundleItemQty(main) : null
  }

  return fromMatching ? normalizeComboBundleItemQty(fromMatching) : null
}

/**
 * @description 构建套餐组 key → leader `comboVO` 映射
 * @param {Array<Object>} shopCartItems
 * @returns {Map<string, Object>}
 */
export const buildComboVOByType3GroupKeyMap = (shopCartItems) => {
  const items = Array.isArray(shopCartItems) ? shopCartItems : []
  const map = new Map()
  for (const line of items) {
    const gk = type3PackageGroupKeyFromRow(line)
    if (!gk || line.comboVO == null) continue
    if (!map.has(gk)) map.set(gk, line.comboVO)
  }
  return map
}

/**
 * @description 扁平行是否套餐活动行且已解析到 `comboProdMeta`
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const isCartType3ComboLineWithMeta = (record) =>
  isComboPackage(record?.promotionType) &&
  record?.comboGroupKey != null &&
  record?.comboGroupKey !== '' &&
  record?.comboProdMeta != null

/**
 * @description 删除主商品或必选搭配品的最后一个 SKU 时需删除整个套餐实例
 * @param {Object|null|undefined} record - 扁平行
 * @param {Array<Object>} items - 当前购物车扁平行；用于判断同商品是否仍有其他 SKU
 * @returns {boolean}
 */
export const cartComboLineDeleteRequiresWholeGroup = (record, items = []) => {
  if (!isCartLinePromotionActivityInProgress(record)) return false
  let requiredProduct = record?.comboDeleteRequiresWholeGroup === true
  if (!isCartType3ComboLineWithMeta(record)) {
    if (isComboBundle(record?.promotionType) && record?.comboGroupKey) {
      requiredProduct ||= (
        !!record?.isComboPackageMain ||
        record?.comboRequired === true ||
        record?.comboRequired === 1
      )
    }
  } else {
    requiredProduct ||=
      !!record.isComboPackageMain ||
      record.comboRequired === true ||
      record.comboRequired === 1 ||
      isComboMatchingProdRequired(record.comboProdMeta)
  }
  if (!requiredProduct) return false

  const groupKey = record?.comboGroupKey || type3PackageGroupKeyFromRow(record)
  const rows = Array.isArray(items) ? items : []
  const removeIndex = rows.indexOf(record)
  const groupIndexes = rows
    .map((item, index) => {
      const itemGroupKey = item?.comboGroupKey || type3PackageGroupKeyFromRow(item)
      return groupKey && itemGroupKey === groupKey ? index : -1
    })
    .filter((index) => index >= 0)
  const hasSiblingSku = hasSiblingSkuForSameProduct(
    rows,
    groupIndexes,
    removeIndex,
    record
  )
  return !hasSiblingSku
}

/**
 * @description 同一套餐实例内所有 basketId
 * @param {Array<Object>} items - 扁平行列表
 * @param {Object} record - 当前行
 * @returns {Array<string|number>}
 */
export const getCartComboGroupBasketIds = (items, record) => {
  const key = record?.comboGroupKey || type3PackageGroupKeyFromRow(record)
  if (!key) return record?.id != null ? [record.id] : []
  return (Array.isArray(items) ? items : [])
    .filter((i) => (i.comboGroupKey || type3PackageGroupKeyFromRow(i)) === key)
    .map((i) => i.id ?? i.basketId)
    .filter((id) => id != null && id !== '')
}

/**
 * @description 套餐行起购件数（`comboVO` 配置优先）
 * @param {Object|null|undefined} comboMeta
 * @param {number} fallbackMin
 * @returns {number}
 */
export const resolveCartComboLineMinPurchaseQty = (comboMeta, fallbackMin = 1) => {
  if (comboMeta && typeof comboMeta === 'object') {
    return resolveComboProdLeastNum(comboMeta)
  }
  const n = Math.trunc(Number(fallbackMin))
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/**
 * @description 套餐行数量上限：`min(maxNum, stock)`
 * @param {Object|null|undefined} comboMeta
 * @param {number} stock
 * @returns {number|null} 非套餐行返回 null，由调用方仅用库存
 */
export const resolveCartComboLineMaxPurchaseQty = (comboMeta, stock) => {
  if (!comboMeta || typeof comboMeta !== 'object') return null
  const maxFromCombo = resolveComboProdMaxNum(comboMeta)
  const comboStock = resolveComboProdStockCap(comboMeta)
  const stockN =
    comboStock !== null
      ? comboStock
      : (() => {
          const n = Math.trunc(Number(stock))
          return Number.isFinite(n) && n > 0 ? n : maxFromCombo
        })()
  const stockCap = stockN > 0 ? stockN : maxFromCombo
  return Math.min(maxFromCombo, stockCap)
}

/**
 * @description 为 V3 行写入套餐约束字段（供 `mapV3ShopCartLineToFlatItem`）
 * @param {Object} line
 * @param {Object|null|undefined} comboVO
 * @param {number} stock
 * @param {number} fallbackMinPurchaseQty
 * @returns {{ comboProdMeta: Object|null, minPurchaseQty: number, comboProductMinPurchaseQty: number, comboMaxPurchaseQty: number|null, comboRequired: boolean, comboDeleteRequiresWholeGroup: boolean }}
 */
export const buildCartComboLineConstraintFields = (
  line,
  comboVO,
  stock,
  fallbackMinPurchaseQty
) => {
  const comboProdMeta = resolveCartComboProdMetaFromComboVO(line, comboVO)
  const isMain =
    line?.comboVO != null ||
    line?.isComboPackageMain === true ||
    Number(line?.comboItemType) === 1
  const comboRequired =
    isMain ||
    isComboMatchingProdRequired(comboProdMeta) ||
    line?.comboRequired === true ||
    line?.comboRequired === 1 ||
    line?.comboRequired === '1'

  const directStartNum = Math.trunc(
    Number(
      line?.comboStartNum ??
        line?.combo_start_num ??
        line?.startNum ??
        line?.leastNum ??
        fallbackMinPurchaseQty
    )
  )
  const directMinPurchaseQty =
    Number.isFinite(directStartNum) && directStartNum >= 1
      ? directStartNum
      : fallbackMinPurchaseQty
  const comboProductMinPurchaseQty = comboProdMeta
    ? resolveCartComboLineMinPurchaseQty(comboProdMeta, directMinPurchaseQty)
    : directMinPurchaseQty
  const minPurchaseQty = 1

  const directMaxNum = Math.trunc(
    Number(line?.comboMaxNum ?? line?.combo_max_num ?? line?.maxNum)
  )
  const directMaxPurchaseQty =
    Number.isFinite(directMaxNum) && directMaxNum >= 1
      ? Math.min(
          directMaxNum,
          Number.isFinite(Number(stock)) && Number(stock) >= 0 ? Number(stock) : directMaxNum
        )
      : null
  const comboMaxPurchaseQty = comboProdMeta
    ? resolveCartComboLineMaxPurchaseQty(comboProdMeta, stock)
    : directMaxPurchaseQty

  const comboDeleteRequiresWholeGroup = isMain || comboRequired

  return {
    comboProdMeta,
    minPurchaseQty,
    comboProductMinPurchaseQty,
    comboMaxPurchaseQty,
    comboRequired,
    comboDeleteRequiresWholeGroup,
  }
}
